import { useTranslation } from 'react-i18next'
import { Chip } from '@structural-climbing/ui'
import { ClassCard, EventCard } from './ClassCard'
import type { CalendarEvent, ClassSession } from './types'
import { dayLabel } from './weekRange'

type Item = { kind: 'class'; session: ClassSession } | { kind: 'event'; event: CalendarEvent }

interface ViewProps {
  days: string[]
  today: string
  classes: ClassSession[]
  events: CalendarEvent[]
  onOpenClass: (session: ClassSession) => void
  onOpenEvent: (event: CalendarEvent) => void
}

function itemsOn(day: string, classes: ClassSession[], events: CalendarEvent[]): Item[] {
  const items: (Item & { at: string })[] = [
    ...classes
      .filter((c) => c.starts_at.startsWith(day))
      .map((session) => ({ kind: 'class' as const, session, at: session.starts_at })),
    ...events
      .filter((e) => e.starts_at.startsWith(day))
      .map((event) => ({ kind: 'event' as const, event, at: event.starts_at })),
  ]
  return items.sort((a, b) => a.at.localeCompare(b.at))
}

function ItemCard({
  item,
  onOpenClass,
  onOpenEvent,
}: { item: Item } & Pick<ViewProps, 'onOpenClass' | 'onOpenEvent'>) {
  return item.kind === 'class' ? (
    <ClassCard session={item.session} onPress={() => onOpenClass(item.session)} />
  ) : (
    <EventCard event={item.event} onPress={() => onOpenEvent(item.event)} />
  )
}

/**
 * Seven columns, each a stack of cards in time order. No hour grid: a gym's
 * day is a handful of classes, and empty hours only push them off the screen.
 */
export function WeekView({ days, today, classes, events, onOpenClass, onOpenEvent }: ViewProps) {
  const { i18n } = useTranslation()
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="week-view">
      {days.map((day) => {
        const label = dayLabel(day, i18n.language)
        const isToday = day === today
        return (
          <section
            key={day}
            className="flex min-w-0 flex-col gap-2"
            aria-label={`${label.weekday} ${label.date}`}
          >
            <header
              className={`rounded-medium px-2 py-1.5 text-center ${isToday ? 'bg-primary text-primary-foreground' : 'bg-content2'}`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide">{label.weekday}</p>
              <p className="text-sm">{label.date}</p>
            </header>
            {itemsOn(day, classes, events).map((item) => (
              <ItemCard
                key={
                  item.kind === 'class' ? item.session.id : `${item.event.kind}-${item.event.id}`
                }
                item={item}
                onOpenClass={onOpenClass}
                onOpenEvent={onOpenEvent}
              />
            ))}
          </section>
        )
      })}
    </div>
  )
}

/** The same week as a list of days, for a phone. Days with nothing on are left out. */
export function AgendaView({ days, today, classes, events, onOpenClass, onOpenEvent }: ViewProps) {
  const { t, i18n } = useTranslation()
  const withItems = days
    .map((day) => ({ day, items: itemsOn(day, classes, events) }))
    .filter((d) => d.items.length > 0)

  if (!withItems.length)
    return <p className="py-10 text-center text-sm text-foreground/50">{t('calendar.emptyWeek')}</p>

  return (
    <div className="flex flex-col gap-5" data-testid="agenda-view">
      {withItems.map(({ day, items }) => {
        const label = dayLabel(day, i18n.language)
        return (
          <section key={day} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold capitalize">{label.weekday}</h2>
              <p className="text-sm text-foreground/60">{label.date}</p>
              {day === today && (
                <Chip size="sm" color="primary" variant="flat">
                  {t('calendar.today')}
                </Chip>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => (
                <ItemCard
                  key={
                    item.kind === 'class' ? item.session.id : `${item.event.kind}-${item.event.id}`
                  }
                  item={item}
                  onOpenClass={onOpenClass}
                  onOpenEvent={onOpenEvent}
                />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}
